import React, {FunctionComponent, useState} from 'react';
import {Button, ButtonGroup, Icon} from '@blueprintjs/core';
import {IconNames} from '@blueprintjs/icons';

import {getIconFor} from '../../../utils/custom-icons';
import {Loading} from './Loading';
import ContentCardContextProvider from './ContentCardContext';
import VerboseName from './VerboseName';
import BlockOutput from './BlockOutput';

import './ContentCard.scss';

interface ContentCardProps {
  type: string,
  contentId: any,
  disabled?: boolean,
  allowCollapse?: boolean,
  showHeader?: boolean,
  showFooter?: boolean,
  children: React.ReactNode,
  onShow?: (value: boolean) => void,
  noContentElement?: React.ReactNode | null | undefined,
}

const CUSTOM_ICON_PROPS: any = {height: 16, width: 16};

const LoadingContent = () => {
  return (
    <div className="content-card">
      <div className="content-card__left"></div>
      <div className="content-card__content show">
        <Loading/>
      </div>
    </div>
  )
};

export const ContentCard: FunctionComponent<ContentCardProps> = ({
  type,
  contentId,
  disabled = false,
  onShow,
  noContentElement = undefined,
  allowCollapse = true,
  showHeader = false,
  showFooter = false,
  children,
}: ContentCardProps) => {
  const [show, setShow] = useState(true);

  const setOnShow = (value: boolean) => {
    setShow(value);
    onShow && onShow(value);
  }

  if (!contentId) {
    return <LoadingContent/>;
  }

  const isContent = !noContentElement;

  return (
    <div className="content-card">
      {
        showHeader && (
          <div className="content-card__header">
            <Icon icon={getIconFor(type, CUSTOM_ICON_PROPS)}/>
            <VerboseName uid={contentId} disabled={disabled}/>
          </div>
        )
      }
      <ContentCardContextProvider isVisible={show}>
        <div className="content-card__body">
          <div className="content-card__left">
            {
              allowCollapse && (
                <ButtonGroup>
                  <Button
                    icon={show ? IconNames.CHEVRON_DOWN : IconNames.CHEVRON_RIGHT}
                    minimal={true}
                    title="Hide content"
                    onClick={() => setOnShow(!show)}
                    small={true}
                    disabled={disabled}
                  />
                </ButtonGroup>
              )
            }
          </div>
          <div className={`content-card__content ${show ? 'show' : 'hide'}`}>
            {!isContent && noContentElement}
            {isContent && children}
          </div>
        </div>
      </ContentCardContextProvider>
      {
        showFooter && (
          <div className="content-card__footer">
            <BlockOutput uid={contentId}/>
          </div>
        )
      }
    </div>
  );
};

export default React.memo(ContentCard);
